import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Mobile breakpoint matches theme (max-width: 749px)
   * @type {MediaQueryList}
   */
  #mobileMQ = window.matchMedia('(max-width: 749px)');

  /**
   * Track whether we attached the scroll listener
   * @type {boolean}
   */
  #scrollListenerAttached = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
        return;
      }

      // Mobile override for scroll-up: do not let observer gating interfere
      if (this.#isMobileScrollUpOverride()) {
        return;
      }

      this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * True when we want scroll-up mode to behave like "always visible" on mobile
   * @returns {boolean}
   */
  #isMobileScrollUpOverride = () => {
    const stickyMode = this.getAttribute('sticky');
    return stickyMode === 'scroll-up' && this.#mobileMQ.matches;
  };

  /**
   * Apply / normalize state for mobile override so header doesn't get stuck hidden
   */
  #applyMobileScrollUpOverride = () => {
    if (!this.#isMobileScrollUpOverride()) return;

    // Force visible sticky state without spamming on scroll
    if (this.dataset.stickyState !== 'active') this.dataset.stickyState = 'active';
    if (this.dataset.scrollDirection !== 'none') this.dataset.scrollDirection = 'none';

    this.removeAttribute('data-animating');

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
  };

  #attachScrollListener = () => {
    if (this.#scrollListenerAttached) return;
    document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    this.#scrollListenerAttached = true;
  };

  #detachScrollListener = () => {
    if (!this.#scrollListenerAttached) return;
    document.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollListenerAttached = false;
  };

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');

    // Mobile override: keep listener attached (for menu stability), but do nothing on scroll
    if (this.#isMobileScrollUpOverride()) {
      return;
    }

    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  #handleMQChange = () => {
    const stickyMode = this.getAttribute('sticky');

    if (stickyMode === 'scroll-up') {
      if (this.#isMobileScrollUpOverride()) {
        // On mobile: keep listener attached, just force visible state once
        this.#applyMobileScrollUpOverride();
      } else {
        // On desktop: ensure normal behavior stays enabled
        this.#attachScrollListener();
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Watch breakpoint changes (devtools resize etc.)
    if (this.#mobileMQ.addEventListener) {
      this.#mobileMQ.addEventListener('change', this.#handleMQChange);
    } else {
      // Safari fallback
      this.#mobileMQ.addListener(this.#handleMQChange);
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'always') {
        this.#attachScrollListener();
      } else if (stickyMode === 'scroll-up') {
        // Always attach; mobile handler is a no-op so it won't hide
        this.#attachScrollListener();
        this.#applyMobileScrollUpOverride();
      }
    }

    // Ensure correct initial state
    this.#handleMQChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#detachScrollListener();

    if (this.#mobileMQ.removeEventListener) {
      this.#mobileMQ.removeEventListener('change', this.#handleMQChange);
    } else {
      this.#mobileMQ.removeListener(this.#handleMQChange);
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const kids = headerGroup.children;
          for (let i = 0; i < kids.length; i++) {
            const element = kids[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
